import { AppConfig, PricingTableConfig } from '@r6digital/rapidstor-types/react/entities/Journey';

import { RapidStorOptions } from '@/types';
import {
    BrowserClient,
    defaultStackParser,
    getDefaultIntegrations,
    makeFetchTransport,
    Scope,
} from '@sentry/browser';
import * as Sentry from "@sentry/react";

/**
 * Simple method to check if the current environment is development
 */
export const isDevelopment = () => {
    return import.meta.env.MODE !== 'production';
};

/**
 * Simple method to check if the current environment is production
 */
export const isProduction = () => {
    return import.meta.env.MODE === 'production';
};

export const loggingClient = (): BrowserClient => {
    const integrations = getDefaultIntegrations({}).filter((defaultIntegration) => {
        return !['BrowserApiErrors', 'Breadcrumbs', 'GlobalHandlers'].includes(
            defaultIntegration.name
        );
    });

    integrations.push(Sentry.browserTracingIntegration());
    integrations.push(Sentry.browserProfilingIntegration());

    const client = new BrowserClient({
        dsn: 'https://4f277693ee15aec89d6b09461e94bd74@o1132036.ingest.sentry.io/4506109304307712',
        transport: makeFetchTransport,
        stackParser: defaultStackParser,
        integrations: integrations,
        release: `rapidstor-v2-frontend@${__APP_VERSION__}`,
        environment: isDevelopment() ? 'development' : 'production',
    });

    return client;
};

export const loggingScope = (): Scope => {
    const client = loggingClient();
    const scope = new Scope();
    scope.setClient(client);

    return scope;
};

/**
 * Application startup method, this is where we initialise Sentry and other global settings related to the app
 */
export const initApplication = () => {
    const client = loggingClient();
    client.init();

    if (isDevelopment()) {
        // eslint-disable-next-line no-console
        console.info(
            `%cRapidStor Journey Based Listing [DEV BUILD - v${__APP_VERSION__}]\nSentry: Enabled\nEnvironment: Development`,
            'background: #444; color: #fff; font-size: 16px; text-align:left;padding:12px; display:block; border-radius:8px;'
        );
    } else {
        // eslint-disable-next-line no-console
        console.info(
            `%cRapidStor Journey Based Listing [PRODUCTION BUILD - v${__APP_VERSION__}]\nSentry: Enabled\nEnvironment: Production`,
            'background: #444; color: #fff; font-size: 16px; text-align:left;padding:12px; display:block; border-radius:8px;'
        );
    }

    bootstrapApplicationConfig();
};

export const transform404Error = (error: string): string => {
    return `[DEV] ${error}`;
};

const bootstrapApplicationConfig = () => {
    const root = document.getElementById('rapidstor-v2-frontend');
    const persona = root?.getAttribute('data-persona');
    const moveInUrl = root?.getAttribute('data-moveInUrl');

    const defaultPricingTableOptions: PricingTableConfig = {
        showTaxRow: true,
        style: 'horizontal',
    };

    const appConfigParsed = JSON.parse(
        root?.getAttribute('data-appConfig') ?? ''
    ) as AppConfig;

    const options: RapidStorOptions = {
        baseURL: appConfigParsed.journey.hosted ? '/' : (appConfigParsed.journey.dev
                ? 'https://api.rapidstorapp.com/'
                : 'https://prod.rapidstorapp.com/'),
            token: appConfigParsed.journey.apiToken,
            corpToken: appConfigParsed.journey.corpToken,
            corpCode: appConfigParsed.journey.corpCode,
            locationCode: appConfigParsed.journey.locationCode,
            location: undefined,
            hiddenLocations: [],
            startPosition: appConfigParsed.journey.startPosition
                ? `${appConfigParsed.journey.startPosition}`
                : undefined,
            persona: appConfigParsed.journey.persona || persona || undefined,
            dev: appConfigParsed.journey.dev === true,
            moveInUrl: moveInUrl || undefined,
            unitTypeNames: appConfigParsed.journey.unitTypeNames?.split(',') || undefined,
            unitTypeRegex: appConfigParsed.journey.unitTypeRegex || undefined,
            loqate: appConfigParsed.journey.loqate || undefined,
            language: appConfigParsed.journey.language || 'en',
            configSelector: appConfigParsed.journey.configSelector || false,
            agentDetails: appConfigParsed.journey.agentDetails || undefined,
            optionalMoveInFields: appConfigParsed.journey.optionalMoveInFields || [],
            pricingTable: appConfigParsed.journey.pricingTable
                ? { 
                    ...defaultPricingTableOptions,
                    ...appConfigParsed.journey.pricingTable 
                } 
                : defaultPricingTableOptions
    };

    window.rapidstor_settings = {
        app_config: appConfigParsed,
        rapidstor_options: options
    }
}